import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePromptInsert } from './PromptInsertContext.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { createChipEditor } from '../../../core/utils/chipEditor.ts';
import classnames from '../../../core/utils/classnames.tsx';

const AddIcon = withIcon(faPlus);

const TOKEN_ATTR = 'data-token';
const { createChip, buildNodes, serializeToText } = createChipEditor(TOKEN_ATTR);

// The prompt editor's own purple — the design system has no semantic purple token, so the pair is defined
// once here and reused wherever the prompt UI needs that accent.
export const PROMPT_PURPLE_CLASS = 'bg-[#f3e8ff] text-[#7c3aed]';
// Shared by the palette chips and the assistant's example instructions, so the two rows can't drift apart.
export const PROMPT_CHIP_CLASS =
    'cursor-pointer rounded px-1.5 py-0.5 text-xs font-medium transition-opacity hover:opacity-80';
// The palette chips: the plain name with a plus, the same shape for variables and tools
const PALETTE_CHIP_CLASS = classnames(PROMPT_CHIP_CLASS, 'inline-flex items-center gap-1');
const CHIP_BASE_CLASS = 'inline-block rounded px-1 py-0.5 text-xs font-medium mx-0.5 select-all';
const VARIABLE_CHIP_CLASS = classnames(CHIP_BASE_CLASS, PROMPT_PURPLE_CLASS);
const TOOL_CHIP_CLASS = classnames(CHIP_BASE_CLASS, 'bg-surface-brand-medium-default text-content-brand-strong');

export type PromptVariableCategory = 'caller' | 'client' | 'internalContact' | 'system';

export interface PromptVariable {
    name: string;
    label?: string;
    description?: string;
    category?: PromptVariableCategory;
}

export interface PromptTool {
    name: string;
    description?: string;
}

export interface VoiceAgentPromptEditorProps {
    value: string;
    onValueChange: (value: string) => void;
    variables: PromptVariable[];
    // omitted for texts that cannot reference tools (e.g. the greeting): hides the tool legend and palette
    tools?: PromptTool[];
    placeholder?: string;
    minHeightClass?: string;
    // false: no slash hint and no palette under the field — a page-level floating palette (PromptInsertProvider) serves
    // the editor instead
    palette?: boolean;
}

// ElevenLabs references dynamic variables with the {{ }} template syntax — always wrap variable names in braces.
const variableToken = (name: string) => `{{${name}}}`;

// token: what lands in the text (variables in braces); label: what the palette and the slash menu show (the plain
// name, without braces); group: the palette heading the item is listed under
export type PromptMenuItem = {
    token: string;
    label: string;
    description?: string;
    kind: 'variable' | 'tool';
    group?: PromptVariableCategory;
};
type MenuItem = PromptMenuItem;

export const variablePaletteItems = (variables: PromptVariable[]): PromptMenuItem[] =>
    variables.map((v) => ({
        token: variableToken(v.name),
        label: v.name,
        description: v.label && v.description ? `${v.label}: ${v.description}` : v.description || v.label,
        kind: 'variable' as const,
        group: v.category,
    }));

export const toolPaletteItems = (tools: PromptTool[]): PromptMenuItem[] =>
    tools.map((tool) => ({ token: tool.name, label: tool.name, description: tool.description, kind: 'tool' as const }));

const chipClass = (kind: MenuItem['kind'], className?: string) =>
    classnames(kind === 'variable' ? VARIABLE_CHIP_CLASS : TOOL_CHIP_CLASS, className);

export const VoiceAgentPromptEditor = ({
    value,
    onValueChange,
    variables,
    tools,
    placeholder,
    minHeightClass = 'min-h-[280px]',
    palette = true,
}: VoiceAgentPromptEditorProps) => {
    const { t } = useTranslation('voice_agents');
    const promptInsert = usePromptInsert();
    const editorRef = React.useRef<HTMLDivElement>(null);
    const savedRangeRef = React.useRef<Range | null>(null);
    const lastValueRef = React.useRef(value);
    const isComposing = React.useRef(false);

    const [showMenu, setShowMenu] = useState(false);
    const [menuFilter, setMenuFilter] = useState('');
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [selectedMenuIndex, setSelectedMenuIndex] = useState(0);

    const allItems = React.useMemo<MenuItem[]>(
        () => [...variablePaletteItems(variables), ...toolPaletteItems(tools ?? [])],
        [variables, tools]
    );

    const chipClassByToken = React.useMemo(
        () => new Map<string, string>(allItems.map((item) => [item.token, chipClass(item.kind)])),
        [allItems]
    );

    const filteredItems = React.useMemo(() => {
        if (!menuFilter) return allItems;
        const lower = menuFilter.toLowerCase();
        return allItems.filter(
            (item) => item.token.toLowerCase().includes(lower) || item.label.toLowerCase().includes(lower)
        );
    }, [allItems, menuFilter]);

    // Sync editor when the value changes externally (form reset / template seed).
    useEffect(() => {
        if (!editorRef.current) return;
        const currentText = serializeToText(editorRef.current);
        if (currentText !== value) {
            editorRef.current.replaceChildren(...buildNodes(value, chipClassByToken));
            lastValueRef.current = value;
        }
    }, [value, chipClassByToken]);

    const emitChange = () => {
        if (!editorRef.current) return;
        const text = serializeToText(editorRef.current);
        if (text !== lastValueRef.current) {
            lastValueRef.current = text;
            onValueChange(text);
        }
    };

    const saveSelection = () => {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount || !editorRef.current) return;
        const range = sel.getRangeAt(0);
        if (editorRef.current.contains(range.commonAncestorContainer)) {
            savedRangeRef.current = range.cloneRange();
        }
    };

    const buildChip = (item: MenuItem) => createChip(item.token, chipClass(item.kind));

    const placeCaretAfter = (node: Node) => {
        const sel = window.getSelection();
        if (!sel) return;
        const spacer = window.document.createTextNode(' ');
        node.parentNode?.insertBefore(spacer, node.nextSibling);
        const range = window.document.createRange();
        range.setStartAfter(spacer);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    };

    // Insert from the slash menu: replace the "/filter" with a chip.
    const insertItemFromSlash = (item: MenuItem) => {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount || !editorRef.current) return;
        const range = sel.getRangeAt(0);
        const textNode = range.startContainer;
        if (textNode.nodeType !== Node.TEXT_NODE) return;

        const text = textNode.textContent || '';
        const offset = range.startOffset;
        const slashIdx = text.lastIndexOf('/', offset);
        if (slashIdx < 0) return;

        const before = text.substring(0, slashIdx);
        const after = text.substring(offset);
        const chip = buildChip(item);
        const parent = textNode.parentNode!;
        const beforeNode = window.document.createTextNode(before);
        const afterNode = window.document.createTextNode(after);

        parent.insertBefore(beforeNode, textNode);
        parent.insertBefore(chip, textNode);
        parent.insertBefore(afterNode, textNode);
        parent.removeChild(textNode);
        placeCaretAfter(chip);

        setShowMenu(false);
        setMenuFilter('');
        emitChange();
    };

    // Insert from the palette: at the last caret position inside the editor, otherwise append.
    const insertItemFromPalette = (item: MenuItem) => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.focus();
        const sel = window.getSelection();
        const chip = buildChip(item);

        const range = savedRangeRef.current;
        if (range && editor.contains(range.commonAncestorContainer)) {
            range.deleteContents();
            range.insertNode(chip);
        } else {
            editor.appendChild(chip);
        }
        placeCaretAfter(chip);
        if (sel) savedRangeRef.current = sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
        emitChange();
    };

    const updateMenuFilter = () => {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return setShowMenu(false);
        const range = sel.getRangeAt(0);
        const textNode = range.startContainer;
        if (textNode.nodeType !== Node.TEXT_NODE) return setShowMenu(false);
        const text = textNode.textContent || '';
        const slashIdx = text.lastIndexOf('/', range.startOffset);
        if (slashIdx < 0) return setShowMenu(false);
        const filter = text.substring(slashIdx + 1, range.startOffset);
        if (filter.includes(' ') || filter.includes('\n')) return setShowMenu(false);
        setMenuFilter(filter);
        setSelectedMenuIndex(0);
    };

    const getMenuPositionFromCaret = () => {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount || !editorRef.current) return { top: 0, left: 0 };
        const range = sel.getRangeAt(0).cloneRange();
        range.collapse(true);
        const rect = range.getBoundingClientRect();
        const editorRect = editorRef.current.getBoundingClientRect();
        return { top: rect.bottom - editorRect.top + 4, left: rect.left - editorRect.left };
    };

    const handleInput = () => {
        if (isComposing.current) return;
        emitChange();
        saveSelection();
        if (showMenu) updateMenuFilter();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === '/' && !showMenu) {
            requestAnimationFrame(() => {
                setMenuPosition(getMenuPositionFromCaret());
                setShowMenu(true);
                setMenuFilter('');
                setSelectedMenuIndex(0);
            });
            return;
        }
        if (!showMenu) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedMenuIndex((prev) => Math.min(prev + 1, filteredItems.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedMenuIndex((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            if (filteredItems.length > 0) {
                e.preventDefault();
                insertItemFromSlash(filteredItems[selectedMenuIndex]);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setShowMenu(false);
            setMenuFilter('');
        }
    };

    const handleBlur = () => {
        saveSelection();
        setTimeout(() => {
            setShowMenu(false);
            setMenuFilter('');
        }, 200);
    };

    const isEmpty = !value;

    return (
        <div className="flex flex-col gap-2">
            <div className="relative flex flex-col gap-1">
                {palette && (
                    <div className="flex items-center gap-1.5 text-xs text-content-weak">
                        <span>{t(tools ? 'promptEditor.slashHint' : 'promptEditor.slashHintVariables')}</span>
                        <span className={chipClass('variable', 'text-xs')}>VAR</span>
                        {tools && <span className={chipClass('tool', 'text-xs')}>TOOL</span>}
                    </div>
                )}
                <div className="w-full shadow-inner-sm border border-solid outline-2 outline-transparent border-line-default rounded bg-surface-weak text-sm text-content-strong transition-colors duration-200 hover:border-line-hover focus-within:border-line-focus focus-within:outline-line-weak">
                    <div className="relative">
                        {isEmpty && (
                            <div className="absolute inset-0 px-2 py-1.5 text-sm text-content-weak pointer-events-none">
                                {placeholder}
                            </div>
                        )}
                        <div
                            ref={editorRef}
                            contentEditable="plaintext-only"
                            suppressContentEditableWarning
                            className={classnames(
                                'px-2 py-1.5 overflow-auto outline-none text-sm whitespace-pre-wrap break-words resize-y',
                                minHeightClass
                            )}
                            onInput={handleInput}
                            onKeyDown={handleKeyDown}
                            onFocus={() => promptInsert?.setActiveInsert(insertItemFromPalette)}
                            onKeyUp={saveSelection}
                            onMouseUp={saveSelection}
                            onBlur={handleBlur}
                            onCompositionStart={() => {
                                isComposing.current = true;
                            }}
                            onCompositionEnd={() => {
                                isComposing.current = false;
                                handleInput();
                            }}
                        />
                    </div>
                </div>
                {showMenu && filteredItems.length > 0 && (
                    <div
                        className="absolute z-50 bg-surface-weak border border-solid border-line-weak rounded-md shadow-lg max-h-48 overflow-auto py-1"
                        style={{ top: menuPosition.top, left: 0, right: 0 }}
                    >
                        {filteredItems.map((item, index) => (
                            <div
                                key={`${item.kind}-${item.token}`}
                                className={`px-3 py-1.5 text-sm cursor-pointer flex items-center gap-2 ${index === selectedMenuIndex ? 'bg-surface-strong' : 'hover:bg-surface-strong'}`}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    insertItemFromSlash(item);
                                }}
                                onMouseEnter={() => setSelectedMenuIndex(index)}
                            >
                                <span className={chipClass(item.kind, 'flex-none text-xs uppercase')}>
                                    {item.kind === 'variable' ? 'VAR' : 'TOOL'}
                                </span>
                                <div className="flex flex-col min-w-0">
                                    <div className="font-medium truncate">{item.label}</div>
                                    {item.description && (
                                        <div className="text-xs text-content-weak truncate">{item.description}</div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Visible palette so the user is aware of which variables and tools they can reference */}
            {palette && (
                <PromptPalette
                    title={t('promptEditor.variablesTitle')}
                    hint={t('promptEditor.variablesHint')}
                    items={variablePaletteItems(variables)}
                    onInsert={insertItemFromPalette}
                />
            )}
            {palette && tools && (
                <PromptPalette
                    title={t('promptEditor.toolsTitle')}
                    hint={t('promptEditor.toolsHint')}
                    items={toolPaletteItems(tools)}
                    onInsert={insertItemFromPalette}
                    emptyText={t('promptEditor.noTools')}
                />
            )}
        </div>
    );
};

// Items with a group are listed under one heading per group, in the order the groups first appear
const groupItems = (items: MenuItem[]) =>
    items.reduce<{ group?: PromptVariableCategory; items: MenuItem[] }[]>((groups, item) => {
        const existing = groups.find((entry) => entry.group === item.group);
        if (existing) existing.items.push(item);
        else groups.push({ group: item.group, items: [item] });
        return groups;
    }, []);

const PaletteChips = ({ items, onInsert }: { items: MenuItem[]; onInsert: (item: MenuItem) => void }) => (
    <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
            <button
                key={item.token}
                type="button"
                title={item.description}
                className={chipClass(item.kind, PALETTE_CHIP_CLASS)}
                // onMouseDown (not onClick) so the editor keeps its saved caret/selection.
                onMouseDown={(e) => {
                    e.preventDefault();
                    onInsert(item);
                }}
            >
                <AddIcon className="text-[0.625rem]" />
                {item.label}
            </button>
        ))}
    </div>
);

export const PromptPalette = ({
    title,
    hint,
    items,
    onInsert,
    emptyText,
}: {
    title: string;
    hint: string;
    items: MenuItem[];
    onInsert: (item: MenuItem) => void;
    emptyText?: string;
}) => {
    const { t } = useTranslation('voice_agents');
    return (
        <div className="flex flex-col gap-1.5 rounded-lg border border-line-weak bg-surface-medium p-3">
            <div className="flex flex-col">
                <span className="text-xs font-medium text-content-strong">{title}</span>
                <span className="text-xs text-content-weak">{hint}</span>
            </div>
            {items.length === 0 ? (
                <span className="text-xs text-content-weak">{emptyText}</span>
            ) : (
                groupItems(items).map(({ group, items: groupedItems }) => (
                    <div key={group ?? 'all'} className="flex flex-col gap-1">
                        {group && (
                            <span className="text-xs text-content-medium">
                                {t(`promptEditor.variableCategories.${group}`)}
                            </span>
                        )}
                        <PaletteChips items={groupedItems} onInsert={onInsert} />
                    </div>
                ))
            )}
        </div>
    );
};
